'use client';
import { AnimatePresence, motion } from 'framer-motion';
import { Minus, Plus } from 'lucide-react';
import { useState } from 'react';

export default function FaqSection({ title = 'Frequently Asked Questions', faqs = [] }) {
    const [activeIndex, setActiveIndex] = useState(null);

    const toggleFaq = (index) => {
        setActiveIndex(activeIndex === index ? null : index);
    };

    return (
        <section className="relative">
            <div className="max-w-6xl mx-auto px-6 lg:px-12">
                {/* Heading */}
                <div className="text-center mb-16">
                    <h2 className="section-heading fs-72">{title}</h2>
                </div>

                {/* FAQ Grid */}
                <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-sm:gap-4">
                    {faqs.map((faq, i) => {
                        const isActive = activeIndex === i;
                        return (
                            <motion.div
                                key={i}
                                layout
                                className={`rounded-md bg-primary/10 shadow-md cursor-pointer hover:shadow-md transition-all duration-300 border border-transparent ${
                                    isActive ? 'shadow-lg border-primary/10' : ''
                                }`}
                            >
                                <button
                                    onClick={() => toggleFaq(i)}
                                    className="w-full text-left flex items-start justify-between px-6 max-sm:pb-2 py-6 max-sm:pt-2 cursor-pointer"
                                >
                                    <span
                                        className={`md:text-xl font-semibold ${
                                            isActive ? 'text-neutral-900' : 'text-neutral-800'
                                        }`}
                                    >
                                        {faq.question}
                                    </span>
                                    <motion.span
                                        initial={false}
                                        animate={{ rotate: isActive ? 180 : 0 }}
                                        transition={{
                                            duration: 0.3,
                                            ease: 'easeInOut',
                                        }}
                                        className="ml-3 mt-1 flex-shrink-0 text-primary"
                                    >
                                        {isActive ? <Minus size={22} /> : <Plus size={22} />}
                                    </motion.span>
                                </button>

                                <AnimatePresence initial={false}>
                                    {isActive && (
                                        <motion.div
                                            key="content"
                                            initial={{
                                                opacity: 0,
                                                height: 0,
                                                y: -10,
                                            }}
                                            animate={{
                                                opacity: 1,
                                                height: 'auto',
                                                y: 0,
                                                transition: {
                                                    height: {
                                                        duration: 0.4,
                                                        ease: [0.04, 0.62, 0.23, 0.98],
                                                    },
                                                    opacity: {
                                                        duration: 0.3,
                                                        delay: 0.1,
                                                    },
                                                    y: {
                                                        duration: 0.2,
                                                        delay: 0.1,
                                                    },
                                                },
                                            }}
                                            exit={{
                                                opacity: 0,
                                                height: 0,
                                                y: -5,
                                                transition: {
                                                    height: {
                                                        duration: 0.3,
                                                        ease: [0.04, 0.62, 0.23, 0.98],
                                                    },
                                                    opacity: {
                                                        duration: 0.2,
                                                    },
                                                    y: {
                                                        duration: 0.15,
                                                    },
                                                },
                                            }}
                                            className="overflow-hidden"
                                        >
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{
                                                    duration: 0.2,
                                                    delay: 0.2,
                                                }}
                                                className="px-6 pb-6 leading-relaxed max-sm:text-sm md:text-base"
                                            >
                                                {faq.answer}
                                            </motion.div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
